"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Menu, X } from "lucide-react";

import { Button } from "@/components/ui/button";

const LINKS = [
  { label: "Korzyści", href: "#korzysci" },
  { label: "Moduły", href: "#moduly" },
  { label: "Aplikacja", href: "#aplikacja" },
  { label: "Cennik", href: "#cennik" },
  { label: "FAQ", href: "#faq" },
];

// Floating "fluid island" navigation — a frosted glass pill detached from the
// top edge. Single line on desktop; on mobile the links collapse into a
// hamburger panel so navigation and login stay reachable.
export function SiteNav() {
  const [open, setOpen] = useState(false);

  // Close the mobile menu on Escape and lock body scroll while it is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-50 px-4 pt-4">
      <nav className="glass mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 rounded-2xl px-3 pl-4">
        <Link
          href="/"
          className="flex items-center transition-transform duration-300 ease-out hover:scale-[1.02]"
          aria-label="ERP LVS — strona główna"
        >
          <Image
            src="/brand/lvs-logo-color.png"
            alt="LVS Solutions"
            width={622}
            height={440}
            priority
            className="h-9 w-auto"
          />
        </Link>

        <ul className="hidden items-center gap-0.5 md:flex">
          {LINKS.map((link) => (
            <li key={link.href}>
              <Link
                href={link.href}
                className="group relative rounded-full px-3.5 py-2 text-sm font-medium text-muted-foreground transition-colors duration-300 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {link.label}
                {/* current-trace underline grows from center on hover */}
                <span className="pointer-events-none absolute inset-x-3.5 -bottom-0.5 h-px origin-center scale-x-0 bg-gradient-to-r from-transparent via-primary to-transparent transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-x-100" />
              </Link>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <Button
            render={<Link href="/login" />}
            variant="ghost"
            size="sm"
            className="hidden h-9 px-3 md:inline-flex"
          >
            Zaloguj się
          </Button>
          <Button
            render={<Link href="/rejestracja" />}
            size="sm"
            className="group hidden h-9 gap-1.5 rounded-full pr-1.5 pl-4 transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] active:scale-[0.98] md:inline-flex"
          >
            Rozpocznij za darmo
            <span className="grid size-6 place-items-center rounded-full bg-white/20 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-0.5">
              <ArrowRight className="size-3.5" />
            </span>
          </Button>

          {/* Mobile menu toggle */}
          <button
            type="button"
            aria-label={open ? "Zamknij menu" : "Otwórz menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((v) => !v)}
            className="grid size-10 cursor-pointer place-items-center rounded-full text-foreground transition-colors duration-300 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:hidden"
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu panel */}
      {open ? (
        <div
          id="mobile-menu"
          className="glass mx-auto mt-2 max-w-6xl rounded-2xl p-3 md:hidden"
        >
          <ul className="flex flex-col">
            {LINKS.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="block rounded-xl px-4 py-3 text-sm font-medium text-foreground transition-colors duration-200 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex flex-col gap-2 border-t border-border pt-3">
            <Button
              render={<Link href="/login" onClick={() => setOpen(false)} />}
              variant="outline"
              size="lg"
              className="h-11 w-full rounded-full"
            >
              Zaloguj się
            </Button>
            <Button
              render={<Link href="/rejestracja" onClick={() => setOpen(false)} />}
              size="lg"
              className="h-11 w-full rounded-full"
            >
              Rozpocznij za darmo
            </Button>
          </div>
        </div>
      ) : null}
    </header>
  );
}
